import { createContext } from "react";

import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { Deal } from "@mapmycustomers/shared/types/entity";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/map/types";
import { AttachedFile, DynamicVarVariant, EmailRecipientMissedFields } from "@mapmycustomers/ui";

import { EmailCreationPayload } from "@app/store/api/ApiService";

export interface EmailCreationContextValueState {
  associatedEntities: EmailAssociatedEntity[];
  attachedFiles: AttachedFile[];
  body: string;
  bodyLimitReached: boolean;
  deals: Deal[];
  preview?: EmailPreview;
  previewLoading: boolean;
  previewOpen: boolean;
  previewRecipientId?: EntitiesSupportedByEmailFeature["id"];
  recipients: EntitiesSupportedByEmailFeature[];
  recipientType: EntityTypesSupportedByEmailFeature;
  sending: boolean;
  subject: string;
  template?: EmailTemplate;
  templateChanged: boolean;
  unsubscribeLinkIncluded?: boolean;
}

export interface EmailCreationContextValueActions {
  attachFile(attachedFile: AttachedFile): void;
  createPreview(email: EmailCreationPayload): void;
  deleteAttachedFile(attachedFileId: AttachedFile["id"]): void;
  fetchAssociatedEntities(params: {
    callback?: (associatedEntities: EmailAssociatedEntity[]) => void;
    failureCallback?: () => void;
    recipients: EntitiesSupportedByEmailFeature[];
  }): void;
  hidePreview(): void;
  send(params: {
    callback?: (email: Email) => void;
    email: EmailCreationPayload;
    failureCallback?: (customFieldErrors: CustomFieldError[]) => void;
  }): void;
  setBody: (value: EmailCreationContextValueState["body"]) => void;
  setBodyLimitReached: (value: EmailCreationContextValueState["bodyLimitReached"]) => void;
  setPreviewRecipientId: (value?: EntitiesSupportedByEmailFeature["id"]) => void;
  setRecipients(value: EntitiesSupportedByEmailFeature[]): void;
  setRecipientType(value: EntityTypesSupportedByEmailFeature): void;
  setSending(value: EmailCreationContextValueState["sending"]): void;
  setSubject: (value: EmailCreationContextValueState["subject"]) => void;
  setTemplate(value: EmailCreationContextValueState["template"]): void;
  setTemplateChanged(value: EmailCreationContextValueState["templateChanged"]): void;
  setUnsubscribeLinkIncluded(
    value: EmailCreationContextValueState["unsubscribeLinkIncluded"]
  ): void;
  showPreview(): void;
}

export interface EmailCreationContextValueSelectors {
  attachedFilesSizeLimitReached: boolean;
  defaultBody: string;
  dynamicVarVariants: DynamicVarVariant[];
  emailFilled: boolean;
  recipientMissedFields: EmailRecipientMissedFields;
}

export type EmailCreationContextValue = EmailCreationContextValueState &
  EmailCreationContextValueActions &
  EmailCreationContextValueSelectors;

const CreateEmailContext = createContext<EmailCreationContextValue | null>(null);

CreateEmailContext.displayName = "CreateEmailContext";

export default CreateEmailContext;
